import * as React from 'react';
import { Button, Stack } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AbsencePeriod, AbsencePeriodStat, AbsencePeriodUser } from 'cb-model';
import {
  Display,
  fetchApi,
  IsAllowed,
  List,
  makeResetCache,
  NavIconContainer,
  notifyError,
  notifyResponse,
  PageTitle,
  QueryMulti,
  QuerySingle,
  Section,
  useCacheRecord,
} from 'gossamer-front-end';
import { getFilter } from 'gossamer-unified';
import { RAGStatusChart } from './RAGStatusChart';
import { AttendanceChart } from './AttendanceChart';

export const AbsencePeriodDisplay = (): React.JSX.Element => {
  const params = useParams();
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const [recalculating, setRecalculating] = React.useState<boolean>(false);
  const absencePeriodRecord = useCacheRecord(AbsencePeriod, params.key);

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      const response = await fetchApi('POST', '/main/absence-period-compute', {
        absencePeriodId: parseInt(params.key),
      });

      // Reset cache to refresh the displayed data
      resetCache(AbsencePeriod.id, params.key);
      resetCache(AbsencePeriodUser.id);
      resetCache(AbsencePeriodStat.id);

      // Show success notification with details
      notifyResponse(response, 'Absence period recalculated successfully');
    } catch (e) {
      notifyError(e, 'Failed to recalculate absence period');
    } finally {
      setRecalculating(false);
    }
  };

  return (
    <>
      <NavIconContainer entityId={AbsencePeriod.id} />
      <PageTitle
        verb="Display"
        entity={AbsencePeriod}
        keyValue={params.key}
        subtitle="Details relating to this Absence Period"
      />

      <Section>
        <QuerySingle entity={AbsencePeriod} keyValue={params.key} render={Display} fieldGroup="main" />
        {absencePeriodRecord.data && (
          <Stack direction="horizontal">
            <div style={{ width: '50% ' }}>
              <h4>Members by RAG Status</h4>
              <RAGStatusChart
                greenCount={
                  absencePeriodRecord.data.member_count -
                  absencePeriodRecord.data.members_in_amber_count -
                  absencePeriodRecord.data.members_in_red_count
                }
                amberCount={absencePeriodRecord.data.members_in_amber_count}
                redCount={absencePeriodRecord.data.members_in_red_count}
              />
            </div>
            <div style={{ width: '50% ' }}>
              <h4>Tracked Bookings by Attendance</h4>
              <AttendanceChart
                attendedCount={absencePeriodRecord.data.attended_count}
                absencesCount={absencePeriodRecord.data.absences_count}
                notOccurredCount={
                  absencePeriodRecord.data.booking_count -
                  absencePeriodRecord.data.attended_count -
                  absencePeriodRecord.data.absences_count
                }
              />
            </div>
          </Stack>
        )}
        <Stack direction="horizontal" gap={2}>
          <Button size="sm" variant="success" onClick={handleRecalculate} disabled={recalculating}>
            {recalculating ? 'Recalculating...' : 'Recalculate'}
          </Button>
          <IsAllowed entity={AbsencePeriod} action="update">
            <Link to={`/admin/absence-period/update/${params.key}`}>
              <Button size="sm" variant="primary">
                Update
              </Button>
            </Link>
          </IsAllowed>
          <IsAllowed entity={AbsencePeriod} action="delete">
            <Link to={`/admin/absence-period/delete/${params.key}`}>
              <Button size="sm" variant="danger">
                Delete
              </Button>
            </Link>
          </IsAllowed>
        </Stack>
      </Section>

      <Section title="Absence Statistics" subtitle="Detailed breakdown of absences by reason">
        <QueryMulti
          entity={AbsencePeriodStat}
          alterEntity={(localEntity: typeof AbsencePeriodStat) => {
            localEntity.fields.absence_period_id.listColumn = false;
            localEntity.fields.created_at.listColumn = false;
          }}
          queryOptions={{
            where: {
              and: [getFilter('absence_period_id', 'eq', params.key)],
            },
          }}
          render={List}
        />
      </Section>

      <Section title="Member Status" subtitle="Individual member RAG status for this absence period">
        <QueryMulti
          entity={AbsencePeriodUser}
          alterEntity={(localEntity: typeof AbsencePeriodUser) => {
            localEntity.fields.absence_period_id.listColumn = false;
          }}
          queryOptions={{
            where: {
              and: [getFilter('absence_period_id', 'eq', params.key)],
            },
            order: [
              ['rag_status', 'DESC'],
              ['absence_count', 'DESC'],
            ],
          }}
          render={List}
        />
      </Section>
    </>
  );
};
